import { Button } from "@mui/material";
import React from "react";
import { formatCurrency, formatNumber, str2number } from "../../../libs/helpers";
import styles from "./index.module.scss";
import GeneralDialog from "../../core/GeneralDialog";
import DateCell from "../../core/DateCell";
import InputCell from "../../core/InputCell";
import CloseIcon from '@mui/icons-material/Close';
import { OfficeWorkOption, OfficeWorkFeeOption } from "../../../apis/models/OfficeWorkResponse";
import { InvoiceMasterData } from "../../../apis/models/InvoiceMasterRequest";
import OfficeWorkDialog, { OfficeWorkDialogData } from "../../dialogs/invoiceDetails/OfficeWorkDialog";
import DateWithoutTime from "../../../libs/dateWithoutTime";
import { InvStatus } from "../../../libs/constants";

type FeeType = {
  officeWorkFeeOptionId: string;
  officeWorkFeeOption: OfficeWorkFeeOption;
  quantity: string;
}

export type InputField = {
  dateAdded: DateWithoutTime;
  remarks: string;
  officeWorkFees: FeeType[];
}

type ErrorInfo = {
  dateAdded?: string;
  total?: string;
  officeWorkFees: FeeType[];
}

export type OfficeWorkFeeTabType = InputField & {
  error: ErrorInfo
}

const OfficeWorkFee = (props: OfficeWorkFeeProps) => {
  const [openWarningDialog, setOpenWarningDialog] = React.useState<boolean>(false);
  const [openAddDialog, setOpenAddDialog] = React.useState<boolean>(false);

  const hasError = React.useMemo(() => {
      return props.tabValues.some(fee => {
        if (Object.keys(fee.error).length !== 0) {
          return fee.error.dateAdded || fee.error.total || fee.error.officeWorkFees.length > 0
        } else return false;
      })
  },[props.tabValues]);

  const validateRow = (item: OfficeWorkFeeTabType) => {
    const errorFees = item.officeWorkFees
      .map(fee => {
        return {
          officeWorkFeeOptionId: fee.officeWorkFeeOptionId,
          quantity: validateNumber(fee.quantity),
        } as FeeType
      })
      .filter(fee => fee.quantity !== undefined)

    const error: ErrorInfo = {
      dateAdded: validateDateAdded(item.dateAdded),
      total: calculateTotal(item) <= 0 ? "個数を入力してください" : undefined,
      officeWorkFees: errorFees
    }
    return error
  }

  const validateDateAdded = (value: DateWithoutTime): string | undefined => {
    const dateString = value.getValue4Display()
    if (value.getValue4Display().length === 0) {
      return "入力必須です"
    } else if (value.isInvalid()) {
      return "年月日で入力してください"
    } else if (props.tabValues.filter(item => item.dateAdded.equals(value)).length > 1) {
      return "日付が重複しています"
    } else {
      const invoiceFrom = new DateWithoutTime(props.targetInvoice.from)
      const invoiceTo = new DateWithoutTime(props.targetInvoice.to)
      if (dateString < invoiceFrom.getValue4Display() || invoiceTo.getValue4Display() < dateString) {
        return "請求期間の範囲で入力してください"
      }
    }
    return undefined
  }
  
  const validateNumber = (value: string) => {
    if (value.length === 0) {
      return undefined
    } else if (isNaN(+value) || parseInt(value) < 1) {
      return "１以上を入力してください"
    }
    return undefined
  }

  const calculateTotal = (rowData: OfficeWorkFeeTabType) => {
    return rowData.officeWorkFees.reduce((prev, fee) => prev + str2number(fee.quantity), 0)
  }

  const handleDateChange = (rowIndex: number, date: Date | null) => {
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        fee.dateAdded = new DateWithoutTime(date);
      }
      fee.error = validateRow(fee)
      return fee;
    })
    props.onChange(newItem);
  }

  const handleRemarkChange = (rowIndex: number, value: string | number) => {
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        fee.remarks = value + "";
      }
      fee.error = validateRow(fee)
      return fee;
    })
    props.onChange(newItem);
  }

  const handleInputChange = (rowIndex: number, colIndex: number, val: number) => {
    const optionId = optionMaster[colIndex].officeWorkFeeOptionId;
    const newItem = props.tabValues.map((fee, index) => {
      if (index === rowIndex) {
        if (isNaN(val)) {
          fee.officeWorkFees = fee.officeWorkFees.filter(item => item.officeWorkFeeOptionId !== optionId)
        } else {
          const feeData = fee.officeWorkFees.find(item => item.officeWorkFeeOptionId === optionId)
          if (feeData) {
            fee.officeWorkFees = fee.officeWorkFees.map(item => {
              if (item.officeWorkFeeOptionId === optionId) {
                item.quantity = String(val);
              }
              return item;
            })
          } else {
            fee.officeWorkFees = fee.officeWorkFees.concat([{
              officeWorkFeeOptionId: optionId,
              officeWorkFeeOption: optionMaster[colIndex],
              quantity: String(val),
            } as FeeType])
          }
        }
      }
      fee.error = validateRow(fee)
      return fee;
    })

    props.onChange(newItem);
  }

  const onDelete = (rowIndex: number) => {
    props.onChange(props.tabValues.filter((item, index) => index !== rowIndex))
  }

  const onSaveByOfficeWorkDialog = (data: OfficeWorkDialogData[]) => {
    const addDatas = data.map(d => {
      return {
        dateAdded: d.dateAdded,
        remarks: d.remarks,
        officeWorkFees: d.officeWorkFees,
        error: {}
      } as OfficeWorkFeeTabType
    });
    props.onChange(props.tabValues.concat(addDatas));
    setOpenAddDialog(false)
  }

  const sumByOption = (optionId: string) => {
    let total = 0;
    props.tabValues.forEach(rowItem => {
      const fee = rowItem.officeWorkFees.find(fee => fee.officeWorkFeeOptionId === optionId);
      total = total + str2number(fee?.quantity)
    })
    return total;
  }

  const getCellValue = (rowItem: OfficeWorkFeeTabType, optionId: string) => {
    const fee = rowItem.officeWorkFees.find(fee => fee.officeWorkFeeOptionId === optionId);
    return fee ? fee.quantity : ""
  }

  const totalAmount = React.useMemo(() => {
    let total = 0;
    props.tabValues.forEach(rowItem => {
      rowItem.officeWorkFees.forEach(fee => {
        if(fee.officeWorkFeeOption !== undefined) {
          total = total + str2number(fee.quantity) * fee.officeWorkFeeOption.unitPrice
        }
      })
    })
    return total;
  }, [props.tabValues]);

  const optionMaster = React.useMemo(() => {
    return props.officeWorkCategoryOptions
      .filter(o => o.officeWorkFeeOption.unitPrice > 0 || !o.officeWorkFeeOptionId) // remove category
      .sort((a,b) => (a.order ?? 0) - (b.order ?? 0))
      .map(o => {
        return {
          officeWorkFeeOptionId: o.officeWorkFeeOptionId!,
          workDetails: o.officeWorkFeeOption.workDetails,
          unitPrice: o.officeWorkFeeOption.unitPrice,
          category: o.officeWorkFeeOption.category,
        }
      });
  }, [props.officeWorkCategoryOptions]);

  const optionCategory = React.useMemo(() => {
    return props.officeWorkCategoryOptions
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .reduce((group: {category: string, count: number}[], option: OfficeWorkOption) => {
        if (option.officeWorkFeeOption.unitPrice <= 0) {
          return group
        }
        const key = option.officeWorkFeeOption.category
        const officeWork = group.find(e => e.category === key)
        if (!officeWork) {
          group.push({ category: key, count: 1 })
        } else {
          officeWork.count = officeWork.count + 1
        }
        return group
      }, []);
  }, [props.officeWorkCategoryOptions]);

  const officeWorkExistDates = React.useMemo(() => {
    const existDates: DateWithoutTime[] = [];
    props.tabValues.forEach(d => {
      if (existDates.findIndex(ed => ed.equals(d.dateAdded)) >= 0) {
        return
      }
      existDates.push(d.dateAdded)
    });

    return existDates
  }, [props.tabValues])

  const isConfirmed = React.useMemo(() => {
    return props.targetInvoice?.status === InvStatus.CONFIRMED || props.targetInvoice?.status === InvStatus.EXPORT;
  }, [props.targetInvoice])

  return (
    <>
      <div className={styles.container}>
        <div className={styles.statistics}>
          <div>合計金額：<span>{formatCurrency(totalAmount)}</span></div>
          {isConfirmed ? <Button color="primary" variant="outlined" onClick={() => setOpenWarningDialog(true)}>編集</Button> : <></>}
        </div>
        <div className={styles.table_wrapper}>
          <table>
            <thead>
              <tr className={styles.category}>
                <th colSpan={1}>カテゴリー</th>
                {optionCategory.map((category, index) => (
                  <th key={index} colSpan={category.count}>{category.category}</th>
                ))}
                <th colSpan={1}>備考</th>
                {!isConfirmed ? <td className={styles.small}></td> : <></>}
              </tr>
              <tr className={styles.works}>
                <td>作業内容</td>
                {optionMaster.map(option => (
                  <td key={option.officeWorkFeeOptionId}>{option.workDetails}</td>
                ))}
                <td>-</td>
                {!isConfirmed ? <td className={styles.small}></td> : <></>}
              </tr>
              <tr className={styles.price}>
                <td>単価</td>
                {optionMaster.map(option => (
                  <td key={option.officeWorkFeeOptionId}>{formatNumber(option.unitPrice)}</td>
                ))}
                <td>-</td>
                {!isConfirmed ? <td className={styles.small}></td> : <></>}
              </tr>
              <tr className={styles.total}>
                <td>合計数</td>
                {optionMaster.map(option => (
                  <td key={option.officeWorkFeeOptionId}>{formatNumber(sumByOption(option.officeWorkFeeOptionId))}</td>
                ))}
                <td>-</td>
                {!isConfirmed ? <td className={styles.small}></td> : <></>}
              </tr>
              <tr className={styles.total}>
                <td>合計金額</td>
                {optionMaster.map(option => (
                  <td key={option.officeWorkFeeOptionId}>{formatNumber(sumByOption(option.officeWorkFeeOptionId) * option.unitPrice)}</td>
                ))}
                <td>-</td>
                {!isConfirmed ? <td className={styles.small}></td> : <></>}
              </tr> 
            </thead>
            <tbody>
              {props.tabValues.map((rowItem, rowIndex) => (
                <tr key={rowIndex} className={styles.data}>
                  <td className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                    <DateCell key={rowIndex}
                      isConfirmed={isConfirmed}
                      onChangeDate={(date) => {handleDateChange(rowIndex, date)}}
                      value={rowItem.dateAdded.getValue4Display()}
                    />
                  </td>
                  {
                    optionMaster.map((option, colIndex) => (
                      <td key={colIndex} className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                        <InputCell 
                          isConfirmed={isConfirmed}
                          type="number"
                          onChangeText={(val) => handleInputChange(rowIndex, colIndex, Number(val))}  
                          value={getCellValue(rowItem, option.officeWorkFeeOptionId)}
                          textAlignment="right"
                        />
                      </td>
                    ))
                  }
                  <td className={!isConfirmed ? `${styles.unconfirmed}` : ""}>
                    <InputCell
                      isConfirmed={isConfirmed}
                      type="text"
                      onChangeText={(val) => handleRemarkChange(rowIndex, val)}  
                      value={rowItem.remarks}
                      textAlignment="left"
                    />
                  </td>
                  {!isConfirmed ? <td className={styles.small}><CloseIcon className={styles.close_icon} onClick={() => onDelete(rowIndex)} color="primary"/></td> : <></>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.inner_functions}>
          {!isConfirmed ? <Button color="primary" variant="outlined" onClick={() => setOpenAddDialog(true)}>＋項目追加</Button> : <></>}
        </div>
      </div>
      {/* dialog to warn user when they try to edit a confirmed invoice  ↓ */}
      <GeneralDialog
        open={openWarningDialog} 
        onCancel={() => setOpenWarningDialog(false)} 
        cancelText="キャンセル" 
        onSave={() => {props.onNotYetConfirmed(); setOpenWarningDialog(false)}} 
        saveText="確定を取り消す" 
        title="請求確定取り消し" 
        onClose={() => setOpenWarningDialog(false)}
      >
        <div>
          既に確定済の請求です。<br/>
          内容を編集するには請求の確定取り消しが必要です。<br/><br/>
        </div>
        <div>請求の確定を取り消しますか？</div>
      </GeneralDialog>
      {!isConfirmed ? <div className={styles.outer_functions}>
        <Button variant="outlined" color="primary" onClick={() => props.onReset()} disabled={!props.isChanged}>編集内容を破棄</Button>
        <Button variant="contained" color="secondary" onClick={() => props.onSave()} disabled={hasError || !props.isChanged}>保存</Button>
      </div> : <></>}
      {
        openAddDialog ? (
          <OfficeWorkDialog
            targetBrand={props.targetInvoice.brand}
            targetInvoice={props.targetInvoice}
            officeWorkCategoryOptions={props.officeWorkCategoryOptions}
            onClose={() => setOpenAddDialog(false)}
            open={openAddDialog}
            onSave={(val) => {onSaveByOfficeWorkDialog(val)}}
            existDates={officeWorkExistDates}
          />
        ) : (
          <></>
        )
      }
    </>
  )
}

export default OfficeWorkFee;

export type OfficeWorkFeeProps = {
  targetInvoice: InvoiceMasterData,
  tabValues: OfficeWorkFeeTabType[],
  officeWorkCategoryOptions: OfficeWorkOption[],
  isChanged: boolean,
  onNotYetConfirmed: () => void;
  onReset: () => void;
  onSave: () => void
  onChange: (items: OfficeWorkFeeTabType[]) => void;
}